// Quiz Services
// Firestore quiz operations using ServiceBase

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError, QuizError } from '@/shared/api/errors/ApiError';
import { getCreatedTimestamp, getUpdatedTimestamp, getCurrentISOTimestamp } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes for quiz definitions
const ATTEMPT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for attempts

export interface QuizQuestion {
  id: string;
  text: string;
  options: string[];
  correctAnswer: any;
  scenario?: string;
  explanation?: string;
  [key: string]: any;
}

export interface Quiz {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: QuizQuestion[];
  isFinalExam?: boolean;
  [key: string]: any;
}

export interface QuizAttempt {
  id: string;
  userId: string;
  courseId: string;
  quizId: string;
  quizTitle: string;
  isFinalExam: boolean;
  startedAt: string;
  startedTimestamp: number;
  completedAt?: string;
  completedTimestamp?: number;
  score?: number;
  passed?: boolean;
  correctAnswers?: number;
  totalQuestions?: number;
  timeSpent?: number;
  status: 'started' | 'completed' | 'passed';
  [key: string]: any;
}

export interface FinalExamStatus {
  attemptCount: number;
  passed: boolean;
  lastScore: number;
  lastAttemptDate?: string;
  canRetake: boolean;
  attempts: Array<{
    attemptNumber: number;
    score: number;
    passed: boolean;
    date: string;
  }>;
}

class QuizService extends ServiceBase {
  constructor() {
    super('QuizService');
  }

  private _invalidateQuizCache(courseId: string, quizId: string | null = null): void {
    if (quizId) {
      this.cache.invalidate(`quiz_${courseId}_${quizId}`);
    }
    this.cache.invalidate(`quizzes_${courseId}`);
  }

  private _invalidateAttemptCache(userId: string, courseId: string, quizId: string | null = null): void {
    this.cache.invalidate(`quiz_attempts_${userId}_${courseId}_${quizId || 'all'}`);
    this.cache.invalidate(`quiz_attempts_${userId}_${courseId}_all`);
    this.cache.invalidate(`final_exam_status_${userId}_${courseId}`);
  }

  /**
   * Determines if a quiz should use deterministic upsert (only keep best score)
   * or keep full history (exams, reviews).
   */
  private _shouldUseUpsert(quizId: string, isFinalExam: boolean): boolean {
    if (
      quizId === 'review-mastery-1-5' ||
      quizId === 'review-mastery-6-10' ||
      quizId === 'final-exam-mastery'
    ) {
      return true;
    }
    if (isFinalExam) return false;
    // Explicitly target mastery quizzes for deterministic retention
    return quizId.startsWith('mastery-quiz-');
  }

  /**
   * Create a new quiz attempt
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {Object} quizData Quiz attempt data
   * @returns {Promise<string>} Attempt ID
   */
  async createQuizAttempt(userId: string, courseId: string, quizData: any): Promise<string> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (typeof quizData !== 'object' || !quizData) {
        throw new ValidationError('Quiz data must be a valid object');
      }

      const isUpsertQuiz = this._shouldUseUpsert(quizData.quizId, quizData.isFinalExam);

      let existingAttempt: QuizAttempt | null = null;
      if (isUpsertQuiz) {
        try {
          existingAttempt = await this.getDoc(`users/${userId}/quizAttempts`, quizData.quizId) as QuizAttempt;
        } catch (err: any) {
          // If NOT_FOUND or similar, it means no prior attempt exists, which is expected
          const errCode = err?.code || err?.status;
          if (errCode !== 'NOT_FOUND') {
            this.log(`Error checking existing attempt for ${quizData.quizId}: ${err?.message}`);
          }
        }
      }

      const newAttempt: any = {
        userId,
        courseId,
        quizId: quizData.quizId,
        quizTitle: quizData.quizTitle,
        isFinalExam: quizData.isFinalExam || false,
        startedAt: getCurrentISOTimestamp(),
        startedTimestamp: Date.now(),
        sessionId: quizData.sessionId || null,
        ipAddress: quizData.ipAddress || null,
        deviceInfo: quizData.deviceInfo || null,
        status: 'started',
        ...getCreatedTimestamp()
      };

      if (isUpsertQuiz && existingAttempt) {
        const wasCompleted = existingAttempt.status === 'completed';
        if (wasCompleted) {
          newAttempt.previousBestScore = existingAttempt.score || 0;
          newAttempt.previousWasCompleted = true;
          newAttempt.previousPassed = existingAttempt.passed || false;
          newAttempt.previousCorrectAnswers = existingAttempt.correctAnswers || 0;
          newAttempt.previousTotalQuestions = existingAttempt.totalQuestions || 0;
          newAttempt.previousAnswers = existingAttempt.answers || null;
          newAttempt.previousCompletedAt = existingAttempt.completedAt || null;
          newAttempt.previousCompletedTimestamp = existingAttempt.completedTimestamp || null;
        } else if (existingAttempt.previousWasCompleted) {
          newAttempt.previousBestScore = existingAttempt.previousBestScore || 0;
          newAttempt.previousWasCompleted = true;
          newAttempt.previousPassed = existingAttempt.previousPassed || false;
          newAttempt.previousCorrectAnswers = existingAttempt.previousCorrectAnswers || 0;
          newAttempt.previousTotalQuestions = existingAttempt.previousTotalQuestions || 0;
          newAttempt.previousAnswers = existingAttempt.previousAnswers || null;
          newAttempt.previousCompletedAt = existingAttempt.previousCompletedAt || null;
          newAttempt.previousCompletedTimestamp = existingAttempt.previousCompletedTimestamp || null;
        }
      }

      if (isUpsertQuiz) {
        // Use deterministic ID for mastery quizzes
        await this.setDoc(`users/${userId}/quizAttempts`, quizData.quizId, newAttempt);
        this._invalidateAttemptCache(userId, courseId, quizData.quizId);
        this.log(`Created/Refreshed mastery quiz attempt for ${quizData.quizId}`);
        return quizData.quizId;
      } else {
        const docRef = await addDoc(collection(db, 'users', userId, 'quizAttempts'), newAttempt);
        this._invalidateAttemptCache(userId, courseId, quizData.quizId);
        this.log(`Created unique quiz attempt for ${quizData.quizId} (ID: ${docRef.id})`);
        return docRef.id;
      }
    } catch (error) {
      this.logError(error, { method: 'createQuizAttempt', userId, courseId });
      throw error;
    }
  }

  /**
   * Update an existing quiz attempt
   * @param {string} userId User ID
   * @param {string} attemptId Attempt ID
   * @param {Object} attemptData Data to update
   * @returns {Promise<void>}
   */
  async updateQuizAttempt(userId: string, attemptId: string, attemptData: Partial<QuizAttempt>): Promise<void> {
    try {
      this.validate.validateUserId(userId);
      if (!attemptId || typeof attemptId !== 'string') {
        throw new ValidationError('Attempt ID is required');
      }
      if (typeof attemptData !== 'object' || !attemptData) {
        throw new ValidationError('Attempt data must be a valid object');
      }

      const updateData = {
        ...attemptData,
        ...getUpdatedTimestamp()
      };

      await this.updateDoc(`users/${userId}/quizAttempts`, attemptId, updateData);
    } catch (error) {
      this.logError(error, { method: 'updateQuizAttempt', userId, attemptId });
      throw error;
    }
  }

  /**
   * Submit a quiz attempt
   * @param {string} userId User ID
   * @param {string} attemptId Attempt ID
   * @param {Object} submissionData Submission data
   * @returns {Promise<Object>} Results summary
   */
  async submitQuizAttempt(userId: string, attemptId: string, submissionData: any): Promise<any> {
    try {
      this.validate.validateUserId(userId);
      if (!attemptId || typeof attemptId !== 'string') {
        throw new ValidationError('Attempt ID is required');
      }
      if (typeof submissionData !== 'object' || !submissionData) {
        throw new ValidationError('Submission data must be a valid object');
      }

      // 1. Fetch current attempt data
      const attemptData = await this.getDoc(`users/${userId}/quizAttempts`, attemptId) as QuizAttempt;
      const isUpsertQuiz = this._shouldUseUpsert(attemptData.quizId, attemptData.isFinalExam);

      // 2. Calculate results
      const answers = submissionData.answers || {};
      const totalQuestions = submissionData.totalQuestions || 0;
      const correctAnswers = submissionData.correctAnswers || 0;
      const score = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
      const PASSING_SCORE = (
        attemptData.quizId === 'review-mastery-1-5' ||
        attemptData.quizId === 'review-mastery-6-10' ||
        attemptData.quizId === 'final-exam-mastery'
      ) ? 75 : 70;
      const passed = score >= PASSING_SCORE;

      // 3. Conditional update for mastery (upsert) quizzes
      if (isUpsertQuiz) {
        const currentBestScore = attemptData.status === 'completed'
          ? (attemptData.score || 0)
          : (attemptData.previousBestScore || 0);
        const wasAlreadyAnswered = attemptData.status === 'completed' || !!attemptData.previousWasCompleted;

        if (wasAlreadyAnswered && score <= currentBestScore) {
          this.log(`Result for ${attemptData.quizId} (ID: ${attemptId}) not improved (${score}% <= ${currentBestScore}%). Restoring existing record.`);
          
          const restoreData = {
            status: 'completed',
            score: currentBestScore,
            passed: attemptData.status === 'completed' ? attemptData.passed : attemptData.previousPassed,
            correctAnswers: attemptData.status === 'completed' ? attemptData.correctAnswers : attemptData.previousCorrectAnswers,
            totalQuestions: attemptData.status === 'completed' ? attemptData.totalQuestions : attemptData.previousTotalQuestions,
            answers: attemptData.status === 'completed' ? (attemptData.answers || {}) : (attemptData.previousAnswers || {}),
            completedAt: attemptData.status === 'completed' ? attemptData.completedAt : (attemptData.previousCompletedAt || getCurrentISOTimestamp()),
            completedTimestamp: attemptData.status === 'completed' ? (attemptData.completedTimestamp || Date.now()) : (attemptData.previousCompletedTimestamp || Date.now()),
            // Clear backup fields since we are restoring to completed state
            previousBestScore: null,
            previousWasCompleted: null,
            previousPassed: null,
            previousCorrectAnswers: null,
            previousTotalQuestions: null,
            previousAnswers: null,
            previousCompletedAt: null,
            previousCompletedTimestamp: null,
            ...getUpdatedTimestamp()
          };

          await this.updateDoc(`users/${userId}/quizAttempts`, attemptId, restoreData);
          this._invalidateAttemptCache(userId, attemptData.courseId, attemptData.quizId);

          return {
            attemptId,
            score: currentBestScore,
            passed: restoreData.passed,
            correctAnswers: restoreData.correctAnswers,
            totalQuestions: restoreData.totalQuestions,
            skippedUpdate: true
          };
        }
      }

      const updateData: any = {
        answers,
        totalQuestions,
        correctAnswers,
        score,
        passed,
        completedAt: getCurrentISOTimestamp(),
        completedTimestamp: Date.now(),
        timeSpent: submissionData.timeSpent || 0,
        status: 'completed',
        ...getUpdatedTimestamp()
      };

      if (isUpsertQuiz) {
        updateData.previousBestScore = null;
        updateData.previousWasCompleted = null;
        updateData.previousPassed = null;
        updateData.previousCorrectAnswers = null;
        updateData.previousTotalQuestions = null;
        updateData.previousAnswers = null;
        updateData.previousCompletedAt = null;
        updateData.previousCompletedTimestamp = null;
      }

      await this.updateDoc(`users/${userId}/quizAttempts`, attemptId, updateData);
      this._invalidateAttemptCache(userId, attemptData.courseId, attemptData.quizId);

      return {
        attemptId,
        score,
        passed,
        correctAnswers,
        totalQuestions
      };
    } catch (error) {
      this.logError(error, { method: 'submitQuizAttempt', userId, attemptId });
      throw error;
    }
  }

  private _inFlightAttempts: Map<string, Promise<QuizAttempt[]>> = new Map();

  /**
   * Get quiz attempts for a user
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} quizId Optional Quiz ID
   * @returns {Promise<QuizAttempt[]>} List of attempts
   */
  async getQuizAttempts(userId: string, courseId: string, quizId: string | null = null): Promise<QuizAttempt[]> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      const cacheKey = `quiz_attempts_${userId}_${courseId}_${quizId || 'all'}`;

      // 1. Check persistent result cache
      const cached = this.cache.get<QuizAttempt[]>(cacheKey);
      if (cached) return cached;

      // 2. Check in-flight promise cache (deduplication)
      const inFlight = this._inFlightAttempts.get(cacheKey);
      if (inFlight) {
        console.log(`[QuizService] Deduplicating in-flight request for: ${cacheKey}`);
        return inFlight;
      }

      // 3. Start new request
      const requestPromise = (async () => {
        try {
          const attemptsRef = collection(db, 'users', userId, 'quizAttempts');
          let q;

          if (quizId) {
            q = query(
              attemptsRef,
              where('courseId', '==', courseId),
              where('quizId', '==', quizId)
            );
          } else {
            q = query(
              attemptsRef,
              where('courseId', '==', courseId)
            );
          }

          const snapshot = await getDocs(q);
          const attempts = snapshot.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as object)
          })) as QuizAttempt[];

          // Sort in-memory to prevent missing composite index errors in Firestore
          attempts.sort((a, b) => {
            const timeA = a.startedTimestamp || (a.startedAt ? new Date(a.startedAt).getTime() : 0);
            const timeB = b.startedTimestamp || (b.startedAt ? new Date(b.startedAt).getTime() : 0);
            return timeB - timeA;
          });

          this.cache.set(cacheKey, attempts, ATTEMPT_CACHE_TTL);
          return attempts;
        } finally {
          // Clean up in-flight tracker regardless of success/fail
          this._inFlightAttempts.delete(cacheKey);
        }
      })();

      this._inFlightAttempts.set(cacheKey, requestPromise);
      return requestPromise;
    } catch (error) {
      this.logError(error, { method: 'getQuizAttempts', userId, courseId, quizId });
      throw error;
    }
  }

  /**
   * Get attempt count for a quiz
   */
  async getAttemptCount(userId: string, courseId: string, quizId: string | null = null, isFinalExam = false): Promise<number> {
    try {
      const attempts = await this.getQuizAttempts(userId, courseId, quizId);

      let filteredAttempts = attempts;
      if (isFinalExam) {
        filteredAttempts = attempts.filter(a => a.isFinalExam === true);
      } else if (quizId) {
        filteredAttempts = attempts.filter(a => a.quizId === quizId);
      }

      return filteredAttempts.length;
    } catch (error) {
      this.logError(error, { method: 'getAttemptCount' });
      throw error;
    }
  }

  /**
   * Get last attempt data
   */
  async getLastAttemptData(userId: string, courseId: string, quizId: string | null = null): Promise<QuizAttempt | null> {
    try {
      const attempts = await this.getQuizAttempts(userId, courseId, quizId);
      return attempts.length > 0 ? attempts[0] : null;
    } catch (error) {
      this.logError(error, { method: 'getLastAttemptData' });
      throw error;
    }
  }

  /**
   * Get quiz score summary
   */
  async getQuizScore(userId: string, courseId: string, quizId: string | null = null): Promise<any> {
    try {
      const lastAttempt = await this.getLastAttemptData(userId, courseId, quizId);

      if (!lastAttempt) {
        return { score: 0, passed: false, attempts: 0 };
      }

      return {
        score: lastAttempt.score || 0,
        passed: lastAttempt.passed || false,
        attempts: 1, // Simplified in original
        lastAttemptDate: lastAttempt.completedAt,
        correctAnswers: lastAttempt.correctAnswers || 0,
        totalQuestions: lastAttempt.totalQuestions || 0
      };
    } catch (error) {
      this.logError(error, { method: 'getQuizScore' });
      throw error;
    }
  }

  /**
   * Manually mark a quiz as passed
   */
  async markQuizPassed(userId: string, attemptId: string): Promise<void> {
    try {
      this.validate.validateUserId(userId);
      if (!attemptId || typeof attemptId !== 'string') {
        throw new ValidationError('Attempt ID is required');
      }

      const attemptData = await this.getDoc(`users/${userId}/quizAttempts`, attemptId) as QuizAttempt;

      await this.updateDoc(`users/${userId}/quizAttempts`, attemptId, {
        passed: true,
        status: 'passed',
        ...getUpdatedTimestamp()
      });

      this._invalidateAttemptCache(userId, attemptData.courseId, attemptData.quizId);
    } catch (error) {
      this.logError(error, { method: 'markQuizPassed', userId, attemptId });
      throw error;
    }
  }

  /**
   * Get final exam status for a user/course
   */
  async getFinalExamStatus(userId: string, courseId: string): Promise<FinalExamStatus> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      const cacheKey = `final_exam_status_${userId}_${courseId}`;
      const cached = this.cache.get<FinalExamStatus>(cacheKey);
      if (cached) return cached;

      const attemptsRef = collection(db, 'users', userId, 'quizAttempts');
      const q = query(
        attemptsRef,
        where('courseId', '==', courseId),
        where('isFinalExam', '==', true)
      );

      const snapshot = await getDocs(q);
      const attempts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as object)
      })) as QuizAttempt[];

      // Sort in-memory to prevent missing composite index errors in Firestore
      attempts.sort((a, b) => {
        const timeA = a.startedTimestamp || (a.startedAt ? new Date(a.startedAt).getTime() : 0);
        const timeB = b.startedTimestamp || (b.startedAt ? new Date(b.startedAt).getTime() : 0);
        return timeB - timeA;
      });

      if (attempts.length === 0) {
        const defaultStatus: FinalExamStatus = {
          attemptCount: 0,
          passed: false,
          lastScore: 0,
          canRetake: true,
          attempts: []
        };
        return defaultStatus;
      }

      const passedAttempts = attempts.filter(a => a.passed === true);
      const lastAttempt = attempts[0];

      const status: FinalExamStatus = {
        attemptCount: attempts.length,
        passed: passedAttempts.length > 0,
        lastScore: lastAttempt.score || 0,
        lastAttemptDate: lastAttempt.completedAt,
        canRetake: attempts.length < 3 && !passedAttempts.length,
        attempts: attempts.map((a, index) => ({
          attemptNumber: attempts.length - index,
          score: a.score || 0,
          passed: a.passed || false,
          date: a.completedAt || ''
        }))
      };

      this.cache.set(cacheKey, status, ATTEMPT_CACHE_TTL);
      return status;
    } catch (error) {
      this.logError(error, { method: 'getFinalExamStatus', userId, courseId });
      throw error;
    }
  }

  /**
   * Check if user can retake a quiz
   */
  async canRetakeQuiz(userId: string, courseId: string, quizId: string, isFinalExam = false): Promise<boolean> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      const MAX_ATTEMPTS = 3;
      const attemptCount = await this.getAttemptCount(userId, courseId, quizId, isFinalExam);

      if (attemptCount >= MAX_ATTEMPTS) {
        return false;
      }

      const lastAttempt = await this.getLastAttemptData(userId, courseId, quizId);
      if (lastAttempt && lastAttempt.passed === true) {
        return false;
      }

      return true;
    } catch (error) {
      this.logError(error, { method: 'canRetakeQuiz' });
      throw error;
    }
  }

  /**
   * Get attempt history
   */
  async getQuizAttemptHistory(userId: string, courseId: string, limitCount = 100): Promise<QuizAttempt[]> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (typeof limitCount !== 'number' || limitCount <= 0) {
        throw new ValidationError('Limit must be a positive number');
      }

      const attempts = await this.getQuizAttempts(userId, courseId);
      return attempts.slice(0, limitCount);
    } catch (error) {
      this.logError(error, { method: 'getQuizAttemptHistory', userId, courseId });
      throw error;
    }
  }

  /**
   * Get all quizzes for a course
   */
  async getQuizzes(courseId: string): Promise<Quiz[]> {
    try {
      this.validate.validateCourseId(courseId);

      const cacheKey = `quizzes_${courseId}`;
      const cached = this.cache.get<Quiz[]>(cacheKey);
      if (cached) return cached;

      const quizzesRef = collection(db, 'courses', courseId, 'quizzes');
      // Fetch without orderBy because missing 'order' fields cause empty results in Firestore
      const q = query(quizzesRef);

      const snapshot = await getDocs(q);
      const quizzes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as Quiz[];

      // Sort in memory
      quizzes.sort((a, b) => (a.order || 0) - (b.order || 0));

      this.cache.set(cacheKey, quizzes, CACHE_TTL);
      return quizzes;
    } catch (error) {
      this.logError(error, { method: 'getQuizzes', courseId });
      throw error;
    }
  }

  /**
   * Get quiz definition by ID
   */
  async getQuizById(courseId: string, quizId: string): Promise<Quiz> {
    try {
      this.validate.validateCourseId(courseId);
      if (!quizId || typeof quizId !== 'string') {
        throw new ValidationError('Quiz ID is required');
      }

      const cacheKey = `quiz_${courseId}_${quizId}`;
      const cached = this.cache.get<Quiz>(cacheKey);
      if (cached) return cached;

      const quizData = await this.getDoc(`courses/${courseId}/quizzes`, quizId) as Quiz;

      this.cache.set(cacheKey, quizData, CACHE_TTL);
      return quizData;
    } catch (error: any) {
      if (error.code === 'NOT_FOUND') {
        throw new QuizError('Quiz not found');
      }
      this.logError(error, { method: 'getQuizById', courseId, quizId });
      throw error;
    }
  }

  /**
   * Create a new quiz definition
   */
  async createQuiz(courseId: string, quizData: any): Promise<string> {
    try {
      this.validate.validateCourseId(courseId);

      const docRef = await addDoc(collection(db, 'courses', courseId, 'quizzes'), {
        ...quizData,
        ...getCreatedTimestamp()
      });

      this._invalidateQuizCache(courseId);
      return docRef.id;
    } catch (error) {
      this.logError(error, { method: 'createQuiz', courseId });
      throw error;
    }
  }

  /**
   * Update a quiz definition
   */
  async updateQuiz(courseId: string, quizId: string, updates: Partial<Quiz>): Promise<void> {
    try {
      this.validate.validateCourseId(courseId);

      await this.updateDoc(`courses/${courseId}/quizzes`, quizId, {
        ...updates,
        ...getUpdatedTimestamp()
      });

      this._invalidateQuizCache(courseId, quizId);
    } catch (error) {
      this.logError(error, { method: 'updateQuiz', courseId, quizId });
      throw error;
    }
  }

  /**
   * Get quiz questions (backward compatibility)
   */
  async getQuizQuestions(courseId: string, quizId: string): Promise<QuizQuestion[]> {
    try {
      const quiz = await this.getQuizById(courseId, quizId);
      return quiz.questions || [];
    } catch (error) {
      this.logError(error, { method: 'getQuizQuestions', courseId, quizId });
      throw error;
    }
  }

  /**
   * Submit quiz answers (backward compatibility)
   */
  async submitQuizAnswers(userId: string, courseId: string, quizId: string, answers: any): Promise<any> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!quizId) throw new ValidationError('Quiz ID is required');
      if (!answers) throw new ValidationError('Answers are required');

      const quiz = await this.getQuizById(courseId, quizId);
      const questions = quiz.questions || [];
      let correctCount = 0;

      // Simple scoring logic for backward compatibility
      Object.keys(answers).forEach(qId => {
        const question = questions.find(q => q.id === qId || q.text === qId);
        if (question && question.correctAnswer === answers[qId]) {
          correctCount++;
        }
      });

      const attemptId = await this.createQuizAttempt(userId, courseId, {
        quizId,
        quizTitle: quiz.title,
        isFinalExam: quiz.isFinalExam || false
      });

      return await this.submitQuizAttempt(userId, attemptId, {
        answers,
        totalQuestions: questions.length,
        correctAnswers: correctCount,
        timeSpent: 0
      });
    } catch (error) {
      this.logError(error, { method: 'submitQuizAnswers', userId, courseId, quizId });
      throw error;
    }
  }

  /**
   * Retake quiz (backward compatibility)
   */
  async retakeQuiz(userId: string, courseId: string, quizId: string): Promise<boolean> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!quizId) throw new ValidationError('Quiz ID is required');

      // In the new system, retaking just means starting a new attempt
      this._invalidateAttemptCache(userId, courseId, quizId);
      return true;
    } catch (error) {
      this.logError(error, { method: 'retakeQuiz', userId, courseId, quizId });
      throw error;
    }
  }

  /**
   * Delete a quiz definition
   */
  async deleteQuiz(courseId: string, quizId: string): Promise<void> {
    try {
      this.validate.validateCourseId(courseId);

      await this.deleteDoc(`courses/${courseId}/quizzes`, quizId);
      this._invalidateQuizCache(courseId, quizId);
    } catch (error) {
      this.logError(error, { method: 'deleteQuiz', courseId, quizId });
      throw error;
    }
  }
}

const quizServices = new QuizService();
export default quizServices;

// Maintain backward compatibility
export const createQuizAttempt = (uId: string, cId: string, data: any) => quizServices.createQuizAttempt(uId, cId, data);
export const updateQuizAttempt = (uId: string, aId: string, data: any) => quizServices.updateQuizAttempt(uId, aId, data);
export const submitQuizAttempt = (uId: string, aId: string, data: any) => quizServices.submitQuizAttempt(uId, aId, data);
export const getQuizAttempts = (uId: string, cId: string, qId: string | null = null) => quizServices.getQuizAttempts(uId, cId, qId);
export const getAttemptCount = (uId: string, cId: string, qId: string | null = null, isFinal = false) => quizServices.getAttemptCount(uId, cId, qId, isFinal);
export const getLastAttemptData = (uId: string, cId: string, qId: string | null = null) => quizServices.getLastAttemptData(uId, cId, qId);
export const getQuizScore = (uId: string, cId: string, qId: string | null = null) => quizServices.getQuizScore(uId, cId, qId);
export const markQuizPassed = (uId: string, aId: string) => quizServices.markQuizPassed(uId, aId);
export const getFinalExamStatus = (uId: string, cId: string) => quizServices.getFinalExamStatus(uId, cId);
export const canRetakeQuiz = (uId: string, cId: string, qId: string, isFinal = false) => quizServices.canRetakeQuiz(uId, cId, qId, isFinal);
export const getQuizAttemptHistory = (uId: string, cId: string, limit?: number) => quizServices.getQuizAttemptHistory(uId, cId, limit);
export const getQuizzes = (cId: string) => quizServices.getQuizzes(cId);
export const getQuizById = (cId: string, qId: string) => quizServices.getQuizById(cId, qId);
export const getQuizQuestions = (cId: string, qId: string) => quizServices.getQuizQuestions(cId, qId);
export const submitQuizAnswers = (uId: string, cId: string, qId: string, ans: any) => quizServices.submitQuizAnswers(uId, cId, qId, ans);
export const retakeQuiz = (uId: string, cId: string, qId: string) => quizServices.retakeQuiz(uId, cId, qId);
export const createQuiz = (cId: string, data: any) => quizServices.createQuiz(cId, data);
export const updateQuiz = (cId: string, qId: string, updates: any) => quizServices.updateQuiz(cId, qId, updates);
export const deleteQuiz = (cId: string, qId: string) => quizServices.deleteQuiz(cId, qId);
